/**
 * PersonalizationToggle Component
 *
 * Inline picker that lets the marketer choose whether each {{N}} variable
 * is filled with a STATIC value (same for everyone) or a DYNAMIC field
 * pulled from each user's profile (e.g., {{1}} = user.name).
 *
 * At send time the backend will hydrate dynamic fields per recipient.
 */

import { Sparkles, Type, Database, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";

/** Fields available on every audience user record */
export type DynamicFieldKey =
  | "name"
  | "firstName"
  | "email"
  | "totalGastado"
  | "cantidadPedidos"
  | "userCategory"
  | "lastPurchase"
  | "discountPct"
  | "totalPoints";

export interface DynamicFieldMeta {
  key: DynamicFieldKey;
  label: string;
  example: string;
  category: "identity" | "purchase" | "loyalty";
}

export const DYNAMIC_FIELDS: DynamicFieldMeta[] = [
  { key: "name", label: "Nombre completo", example: "Juan Pérez", category: "identity" },
  { key: "firstName", label: "Primer nombre", example: "Juan", category: "identity" },
  { key: "email", label: "Email", example: "juan@email.com", category: "identity" },
  { key: "totalGastado", label: "Total gastado", example: "12450", category: "purchase" },
  { key: "cantidadPedidos", label: "Cantidad de pedidos", example: "8", category: "purchase" },
  { key: "lastPurchase", label: "Última compra", example: "12 de mayo", category: "purchase" },
  { key: "userCategory", label: "Categoría", example: "VIP", category: "loyalty" },
  { key: "discountPct", label: "% de descuento", example: "15", category: "loyalty" },
  { key: "totalPoints", label: "Puntos acumulados", example: "2540", category: "loyalty" },
];

interface PersonalizationToggleProps {
  variableIndex: number;
  /** Current dynamic field key, or null if static value */
  dynamicField: DynamicFieldKey | null;
  staticValue: string;
  onChange: (value: { dynamicField: DynamicFieldKey | null; staticValue: string }) => void;
}

const CATEGORY_LABEL: Record<DynamicFieldMeta["category"], string> = {
  identity: "Identidad",
  purchase: "Compras",
  loyalty: "Fidelidad",
};

export function PersonalizationToggle({
  variableIndex,
  dynamicField,
  staticValue,
  onChange,
}: PersonalizationToggleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const selectedField = DYNAMIC_FIELDS.find((f) => f.key === dynamicField);

  const groups = DYNAMIC_FIELDS.reduce<
    Record<DynamicFieldMeta["category"], DynamicFieldMeta[]>
  >(
    (acc, f) => {
      acc[f.category] = acc[f.category] || [];
      acc[f.category].push(f);
      return acc;
    },
    { identity: [], purchase: [], loyalty: [] },
  );

  return (
    <div className="flex items-stretch gap-2">
      {/* Mode toggle */}
      <div className="flex bg-slate-100 rounded-xl p-0.5 flex-shrink-0">
        <button
          onClick={() =>
            onChange({ dynamicField: null, staticValue })
          }
          className={`px-2.5 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
            !dynamicField
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
          title="Mismo valor para todos"
        >
          <Type size={12} />
          Texto
        </button>
        <button
          onClick={() =>
            onChange({ dynamicField: "firstName", staticValue: "" })
          }
          className={`px-2.5 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
            dynamicField
              ? "bg-white text-emerald-700 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
          title="Valor distinto por usuario"
        >
          <Database size={12} />
          Dinámico
        </button>
      </div>

      {/* Value editor */}
      {!dynamicField ? (
        <input
          type="text"
          value={staticValue}
          onChange={(e) =>
            onChange({ dynamicField: null, staticValue: e.target.value })
          }
          placeholder={`Valor para {{${variableIndex}}}`}
          className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400
            focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
        />
      ) : (
        <div className="relative flex-1" ref={ref}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-xl transition-all ${
              isOpen
                ? "border-emerald-300 ring-2 ring-emerald-100"
                : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <span className="flex items-center gap-2 text-sm">
              <Sparkles size={13} className="text-emerald-500" />
              <span className="font-medium text-slate-900">
                {selectedField?.label}
              </span>
              <code className="text-[10px] text-slate-400 font-mono">
                user.{selectedField?.key}
              </code>
            </span>
            <ChevronDown
              size={14}
              className={`text-slate-400 transition ${isOpen ? "rotate-180" : ""}`}
            />
          </button>

          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-lg z-30 overflow-hidden max-h-80 overflow-y-auto animate-fade-in">
              {(Object.keys(groups) as DynamicFieldMeta["category"][]).map(
                (cat) => (
                  <div key={cat}>
                    <div className="px-3 py-1.5 bg-slate-50 text-[10px] uppercase tracking-wider font-semibold text-slate-500 sticky top-0">
                      {CATEGORY_LABEL[cat]}
                    </div>
                    {groups[cat].map((field) => (
                      <button
                        key={field.key}
                        onClick={() => {
                          onChange({
                            dynamicField: field.key,
                            staticValue: "",
                          });
                          setIsOpen(false);
                        }}
                        className={`w-full px-3 py-2.5 text-left text-sm hover:bg-emerald-50 transition flex items-center justify-between ${
                          dynamicField === field.key
                            ? "bg-emerald-50 text-emerald-700"
                            : "text-slate-700"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-medium">{field.label}</span>
                          <code className="text-[10px] text-slate-400 font-mono">
                            user.{field.key}
                          </code>
                        </span>
                        <span className="text-[10px] text-slate-400 italic">
                          ej: {field.example}
                        </span>
                      </button>
                    ))}
                  </div>
                ),
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PersonalizationToggle;
